import { canonicalize } from './canonicalize';
import type { AdfNode, HashNodeOptions } from './types';

/**
 * Computes a canonical SHA-256 hash of an ADF node, including its entire
 * subtree (children, marks, attributes, etc.).
 *
 * The node is first converted to a **canonical JSON string** where:
 * - All object keys are sorted alphabetically at every level.
 * - Keys with `undefined` values are stripped.
 * - Keys listed in `options.ignoreKeys` are stripped at every level.
 *
 * The canonical string is then hashed with SHA-256 using the Web Crypto API
 * (`globalThis.crypto.subtle`), and the result is returned as a lowercase
 * hexadecimal string.
 *
 * @param node    - The ADF node to hash. Can be any node including the root
 *                  doc node. The entire subtree is included in the hash.
 * @param options - Optional configuration.
 * @returns A lowercase hexadecimal SHA-256 hash string.
 *
 * @example
 * ```ts
 * import { hashNode } from '@atlassian/adf-node-hasher';
 *
 * const hash = await hashNode({
 *   type: 'paragraph',
 *   content: [{ type: 'text', text: 'Hello world' }],
 * });
 * // => 'a1b2c3d4...' (64 char hex string)
 *
 * // Optionally ignore specific attributes:
 * const hash2 = await hashNode(node, { ignoreKeys: ['someKey'] });
 * ```
 */
export async function hashNode(node: AdfNode, options?: HashNodeOptions): Promise<string> {
	const ignoreKeys = options?.ignoreKeys ? new Set(options.ignoreKeys) : undefined;
	const canonical = canonicalize(node, ignoreKeys);
	return await sha256Hex(canonical);
}

/**
 * Computes the SHA-256 hash of a UTF-8 string and returns it as a lowercase
 * hexadecimal string.
 *
 * Uses `globalThis.crypto.subtle` when available (all modern browsers and
 * Node.js 15+). Falls back to Node.js `crypto` module for environments
 * where the Web Crypto API is not exposed on `globalThis` (e.g. some test
 * runners).
 *
 * @internal
 */
async function sha256Hex(input: string): Promise<string> {
	// Prefer Web Crypto API (browsers + modern Node)
	if (typeof globalThis.crypto?.subtle?.digest === 'function') {
		const encoder = new TextEncoder();
		const data = encoder.encode(input);
		const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
		const hashArray = new Uint8Array(hashBuffer);

		let hex = '';
		for (let i = 0; i < hashArray.length; i++) {
			hex += hashArray[i].toString(16).padStart(2, '0');
		}
		return hex;
	}

	// Fallback: Node.js crypto module
	// eslint-disable-next-line import/no-nodejs-modules, import/dynamic-import-chunkname
	const { createHash } = await import('crypto');
	return createHash('sha256').update(input, 'utf8').digest('hex');
}
